import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isDateRangeLongerThanAllowed } from '../date-range-filter/date-range-filter.util';

type DateValue = string | null;

interface CalendarDay {
    date: Date;
    label: number;
    isOutside: boolean;
}

@Component({
    selector: 'core-ui-input-date-range',
    templateUrl: './input-date-range.component.html',
    styleUrls: ['./input-date-range.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputDateRangeComponent),
            multi: true,
        },
    ],
})
export class InputDateRangeComponent implements ControlValueAccessor, OnDestroy {
    @Input() label: string = 'Fecha';
    @Input() placeholder: string = 'DD/MM/AAAA';
    @Input() disabled: boolean = false;
    @Input() hideChevron: boolean = false;
    @Input() uiMode: 'form' | 'filter' = 'form';
    @Input() iconColor: string = '#5F7081';
    @Input() readOnly: boolean = false;
    @Input() sizeIcon: 'sm' | 'md' | 'base' | 'lg' = 'md';
    @Input() externalTrigger = false;
    @Input() dropdownAlign: 'start' | 'end' = 'end';

    @Output() valueChange = new EventEmitter<DateValue>();

    @ViewChild('control', { read: ElementRef }) controlRef?: ElementRef<HTMLElement>;
    @ViewChild('dropdown', { read: ElementRef }) dropdownRef!: ElementRef<HTMLElement>;

    dropdownStyle: Record<string, string> = {};
    isOpen = false;

    private _value: DateValue = null;
    private externalAnchor?: HTMLElement;
    private readonly onDocumentPointerDown = (event: PointerEvent): void => {
        if (!this.isOpen) return;

        const target = event.target as Node | null;
        if (!target) return;
        if (this.elementRef.nativeElement.contains(target) || this.externalAnchor?.contains(target)) return;

        this.closeDropdown();
    };

    private viewBaseDate: Date;

    hasRange = false;

    displayValue = '';

    currentMonth: Date = new Date();
    monthLabel: string = '';
    monthDays: CalendarDay[] = [];
    weekDays: string[] = ['D', 'L', 'M', 'MI', 'J', 'V', 'S'];

    customStart?: string;
    customEnd?: string;

    private onChange: (value: DateValue) => void = () => { };
    private onTouched: () => void = () => { };

    private readonly minDate: Date;
    private readonly maxDate: Date;

    leftMonthDays: CalendarDay[] = [];
    rightMonthDays: CalendarDay[] = [];

    @Input() maxRangeDays = 30;

    @Input()
    get value(): DateValue {
        return this._value;
    }
    set value(val: DateValue) {
        this._value = val;
    }

    private readonly changeDetector = inject(ChangeDetectorRef);

    constructor(
        private elementRef: ElementRef,
        @Inject(DOCUMENT) private document: Document,
    ) {

        const today = this.stripTime(new Date());
        this.maxDate = today;
        this.minDate = new Date(1970, 0, 1);

        this.viewBaseDate = today;
        this.buildCalendars();
        this.document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    }

    ngOnDestroy(): void {
        this.document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    }

    private addDays(d: Date, days: number): Date {
        const result = new Date(d);
        result.setDate(result.getDate() + days);
        return this.stripTime(result);
    }

    get canGoPrevMonth(): boolean {
        return true;
    }

    private stripTime(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closeDropdown();
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    @HostListener('window:scroll')
    onScroll(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    writeValue(value: DateValue): void {
        this._value = value;
        this.displayValue = value ?? '';
    }

    registerOnChange(fn: (value: DateValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    toggleDropdown(anchor?: HTMLElement): void {
        if (this.disabled) return;

        if (this.isOpen) {
            this.closeDropdown();
            return;
        }

        this.externalAnchor = anchor;
        this.dropdownStyle = {
            position: 'fixed',
            top: '-10000px',
            left: '-10000px',
            visibility: 'hidden',
            pointerEvents: 'none',
        };
        this.isOpen = true;
        this.onTouched();

        this.scheduleReposition();
    }

    closeDropdown(): void {
        this.isOpen = false;
        this.dropdownStyle = {};
        this.externalAnchor = undefined;
    }

    private scheduleReposition(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.repositionFixedDropdown());
        });
    }

    private repositionFixedDropdown(): void {
        const controlEl = this.controlRef?.nativeElement ?? this.externalAnchor;
        const dropdownEl = this.dropdownRef?.nativeElement;
        if (!controlEl || !dropdownEl) return;

        const gap = 8;
        const padding = 8;

        const c = controlEl.getBoundingClientRect();

        const d = dropdownEl.getBoundingClientRect();

        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;

        const spaceTop = c.top;
        const spaceBottom = viewportH - c.bottom;

        const openUp = spaceBottom < d.height + gap && spaceTop >= d.height + gap;

        let top = openUp ? (c.top - d.height - gap) : (c.bottom + gap);
        let left = this.dropdownAlign === 'start' ? c.left : c.right - d.width;

        left = Math.max(padding, Math.min(left, viewportW - d.width - padding));
        top = Math.max(padding, Math.min(top, viewportH - d.height - padding));

        this.dropdownStyle = {
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            right: 'auto',
            bottom: 'auto',
            zIndex: '99999',
            visibility: 'visible',
            pointerEvents: 'auto',
        };
        this.changeDetector.markForCheck();
    }

    private buildCalendars(): void {
        const right = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth(),
            1
        );

        const left = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );

        this.leftMonthDays = this.buildMonthDays(left);
        this.rightMonthDays = this.buildMonthDays(right);
    }

    private buildMonthDays(monthDate: Date): CalendarDay[] {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();

        const firstOfMonth = new Date(year, month, 1);
        const startWeekDay = firstOfMonth.getDay();

        const startDate = new Date(year, month, 1 - startWeekDay);

        const days: CalendarDay[] = [];
        for (let i = 0; i < 42; i++) {
            const current = new Date(startDate);
            current.setDate(startDate.getDate() + i);
            days.push({
                date: this.stripTime(current),
                label: current.getDate(),
                isOutside: current.getMonth() !== month,
            });
        }
        return days;
    }

    onPrevMonth(): void {
        if (!this.canGoPrevMonth) return;

        this.viewBaseDate = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );

        this.buildCalendars();
        if (this.isOpen) this.scheduleReposition();
    }

    onNextMonth(): void {
        if (!this.canGoNextMonth) return;

        if (this.isOpen) this.scheduleReposition();

        this.viewBaseDate = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() + 1,
            1
        );
        this.buildCalendars();
    }

    get canGoNextMonth(): boolean {
        const newBase = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() + 1,
            1
        );
        const newRightMonth = newBase;
        const currentMonth = new Date(
            this.maxDate.getFullYear(),
            this.maxDate.getMonth(),
            1
        );

        return newRightMonth <= currentMonth;
    }

    private formatDate(date: Date): string {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }

    isSelected(date: Date): boolean {
        if (!this._value) return false;
        return this._value === this.formatDate(date);
    }

    get leftMonthLabel(): string {
        const left = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );
        return this.getMonthName(left.getMonth()) + ' ' + left.getFullYear();
    }

    private getMonthName(m: number): string {
        const names = [
            'Enero', 'Febrero', 'Marzo', 'Abril',
            'Mayo', 'Junio', 'Julio', 'Agosto',
            'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        return names[m];
    }

    isStart(d: Date): boolean {
        if (!this.customStart) return false;
        const s = this.parseInputDate(this.customStart);
        return this.isSameDate(d, s);
    }

    isToday(date: Date): boolean {
        return this.isSameDate(date, new Date());
    }

    private isSameDate(a: Date, b: Date): boolean {
        return (
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
        );
    }

    isEnd(d: Date): boolean {
        if (!this.customEnd) return false;
        const e = this.parseInputDate(this.customEnd);
        return this.isSameDate(d, e);
    }

    isInRange(d: Date): boolean {
        if (!this.customStart || !this.customEnd) return false;

        const s = this.parseInputDate(this.customStart);
        const e = this.parseInputDate(this.customEnd);
        const time = this.stripTime(d).getTime();
        return time > s.getTime() && time < e.getTime();
    }

    isDisabledDay(d: Date): boolean {
        const date = this.stripTime(d);
        const today = this.stripTime(new Date());

        if (date > today) {
            return true;
        }

        if (this.customStart && !this.customEnd) {
            const start = this.parseInputDate(this.customStart);

            if (isDateRangeLongerThanAllowed(date, start, this.maxRangeDays)) {
                return true;
            }
        }

        return false;
    }

    isOutOfRangeWindow(d: Date): boolean {
        if (!this.customStart || this.customEnd) return false;

        const date = this.stripTime(d);
        const start = this.parseInputDate(this.customStart);

        return isDateRangeLongerThanAllowed(date, start, this.maxRangeDays);
    }

    onSelectDate(date: Date): void {
        const clicked = this.stripTime(date);

        if (this.isDisabledDay(clicked)) {
            return;
        }

        const iso = this.toInputDateValue(clicked);

        if (!this.customStart || (this.customStart && this.customEnd)) {
            this.customStart = iso;
            this.customEnd = undefined;
        } else {
            const start = this.parseInputDate(this.customStart);
            const end = clicked;

            if (isDateRangeLongerThanAllowed(end, start, this.maxRangeDays)) {
                return;
            }

            if (end < start) {
                this.customEnd = this.customStart;
                this.customStart = this.toInputDateValue(end);
            } else {
                this.customEnd = iso;
            }
        }

        this.hasRange = !!(this.customStart && this.customEnd);

        if (this.customStart && this.customEnd) {
            const s = this.parseInputDate(this.customStart);
            const e = this.parseInputDate(this.customEnd);

            this.displayValue = this.formatRange(s, e);

            this._value = this.displayValue;
            this.onChange(this._value);
            this.valueChange.emit(this._value);

            this.closeDropdown();
        }
    }

    private formatRange(start: Date, end: Date): string {
        const s = this.formatDate(start);
        const e = this.formatDate(end);
        if (s === e) return s;
        return `${s} - ${e}`;
    }

    get rightMonthLabel(): string {
        const right = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth(),
            1
        );
        return this.getMonthName(right.getMonth()) + ' ' + right.getFullYear();
    }

    private toInputDateValue(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${yyyy}-${mm}-${dd}`;
    }

    private parseInputDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    onSelectDay(day: CalendarDay): void {
        const formatted = this.formatDate(day.date);
        this._value = formatted;

        this.onChange(formatted);
        this.onTouched();
        this.valueChange.emit(formatted);

        this.closeDropdown();
    }

    get isPlaceholder(): boolean {
        return !this._value;
    }

    get isFilled(): boolean {
        return this.uiMode === 'form' && (this.value ?? '').trim().length > 0;
    }

    private capitalize(text: string): string {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

}
